import React, { useState, useRef, useEffect } from 'react';
import { FileText, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './VideoAnalysis.css';
import { useAuth } from '../../../hooks/useAuth';
import { getAuth } from 'firebase/auth';
import { CreateAnalysis, FinalizeAnalysis, GetAnalysisStatus } from '../../../Client/ManagementServiceClient';

const STATUS_POLL_INTERVAL_MS = 3000;
// Enough to ride out a redeploy or a brief network drop without giving up on
// an analysis that is still running server-side.
const MAX_STATUS_POLL_FAILURES = 5;

const VideoAnalysis = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [analysisMode, setAnalysisMode] = useState('video'); // 'video' or 'transcript'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef(null);
  const transcriptFileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  // Read through a ref so the poll loop below is not restarted by anything
  // other than the analysis it is watching.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setVideoPreview(e.target?.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    } else {
      setError('Please select a valid video file');
    }
  };

  const handleTranscriptFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const normalizedName = file.name.toLowerCase();
    const allowedExtensions = ['.txt', '.md', '.srt', '.vtt'];
    const hasAllowedExtension = allowedExtensions.some((ext) => normalizedName.endsWith(ext));
    const isTextType = file.type.startsWith('text/') || file.type === 'application/x-subrip';

    if (isTextType || hasAllowedExtension) {
      setTranscriptFile(file);
      setTranscript('');
      setError(null);
      setUploadProgress(0);
    } else {
      setError('Please select a valid text transcript file');
    }
  };

  const parseJsonFromString = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    let text = value.trim();
    const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fenceMatch && fenceMatch[1]) {
      text = fenceMatch[1].trim();
    }

    try {
      return JSON.parse(text);
    } catch {
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;
      try {
        return JSON.parse(jsonMatch[0]);
      } catch {
        return null;
      }
    }
  };

  const averageNumbers = (...values) => {
    const valid = values.filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!valid.length) return null;
    return valid.reduce((sum, v) => sum + v, 0) / valid.length;
  };

  const isTranscriptUpload = analysisMode === 'transcript' && !!transcriptFile;

  /* Poll the analysis until it settles.

     This was a WebSocket, but the server side of it was a 30-second Firestore
     poll forwarded down the socket -- the connection manager it registered
     with was never broadcast to, because the worker that updates the document
     runs in another service entirely. So it was polling either way, just at
     six times the latency and with a Cloud Run connection held open for the
     length of the analysis. */
  useEffect(() => {
    if (!analysisId || !isAnalyzing) return undefined;

    let cancelled = false;
    let consecutiveFailures = 0;

    const stop = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const poll = async () => {
      if (cancelled) return;

      try {
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();
        if (cancelled) return;

        const { status: httpStatus, myJson } = await GetAnalysisStatus(idToken, analysisId);
        if (cancelled) return;

        if (httpStatus !== 200) {
          throw new Error(myJson?.detail || `Status check failed (${httpStatus})`);
        }

        consecutiveFailures = 0;
        const state = String(myJson.status || '').toLowerCase();

        if (state === 'completed') {
          setCurrentStep(5);
          setAnalysisResults({
            ...(myJson.analysis_results || {}),
            analysis: myJson.analysis || myJson.analysis_results?.analysis,
          });
          setIsAnalyzing(false);
          if (!hasNavigatedRef.current) {
            hasNavigatedRef.current = true;
            navigateRef.current(`/AnalysisDetails/${myJson.analysis_id || analysisId}`);
          }
          return;
        }

        if (state === 'failed') {
          setError(`Analysis failed: ${myJson.error || 'Unknown error'}`);
          setIsAnalyzing(false);
          return;
        }

        if (state === 'queued' || state === 'pending') {
          setCurrentStep(3);
        } else if (state === 'processing') {
          setCurrentStep(4);
          if (myJson.progress) {
            setUploadProgress(myJson.progress);
          }
        }
      } catch (pollError) {
        /* A single failed poll is not a failed analysis -- the work carries on
           in the async service regardless of whether this tab can reach the
           API. Only give up once it is clearly not coming back. */
        consecutiveFailures += 1;
        console.error('Analysis status poll failed:', pollError);
        if (consecutiveFailures >= MAX_STATUS_POLL_FAILURES) {
          setError('Lost contact with the analysis service. The analysis is still running — reopen it from your analyses list.');
          setIsAnalyzing(false);
          return;
        }
      }

      if (!cancelled) {
        pollTimerRef.current = setTimeout(poll, STATUS_POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      stop();
    };
  }, [analysisId, isAnalyzing]);

  const handleAnalyze = async () => {
    if (analysisMode === 'video' && !videoFile) {
      setError('Please select a video file');
      return;
    }

    if (analysisMode === 'transcript' && !transcriptFile && !transcript.trim()) {
      setError('Please upload or enter a transcript');
      return;
    }

    if (!user) {
      setError('You must be logged in to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setCurrentStep(1);
    setAnalysisResults(null);

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();

      if (analysisMode === 'transcript') {
        if (transcriptFile) {
          // Transcript file analysis - upload and process like video
          setCurrentStep(1);
          setUploadProgress(0);

          const transcriptContentType = transcriptFile.type || 'text/plain';
          const createResult = await CreateAnalysis(
            idToken,
            transcriptFile.name,
            transcriptContentType,
            'anthropic',
            true
          );

          if (createResult.status !== 200) {
            throw new Error(`Failed to create analysis: ${createResult.myJson.detail}`);
          }

          const newAnalysisId = createResult.myJson.Entities?.[0] || createResult.myJson.analysis_id;
          const uploadUrl = createResult.myJson.upload_url;

          setAnalysisId(newAnalysisId);

          setCurrentStep(2);
          await uploadFileToGCS(uploadUrl, transcriptFile, transcriptContentType);
          console.log('Transcript uploaded successfully to GCS');

          setCurrentStep(3);
          await finalizeAnalysis(newAnalysisId, idToken);
          setCurrentStep(4);
        } else {
          // Direct transcript analysis - skip file upload
          await analyzeTranscriptDirect(idToken);
        }
      } else {
        // Video file analysis
        // Step 1: Create analysis
        setCurrentStep(1);
        const createResult = await CreateAnalysis(
          idToken,
          videoFile.name,
          videoFile.type
        );

        if (createResult.status !== 200) {
          throw new Error(`Failed to create analysis: ${createResult.myJson.detail}`);
        }

        const newAnalysisId = createResult.myJson.Entities?.[0] || createResult.myJson.analysis_id;
        const uploadUrl = createResult.myJson.upload_url;

        setAnalysisId(newAnalysisId);

        // Step 2: Upload file to GCS
        setCurrentStep(2);
        await uploadFileToGCS(uploadUrl, videoFile);
        console.log('File uploaded successfully to GCS');

        // Step 3: queue the analysis now that the upload has landed
        setCurrentStep(3);
        await finalizeAnalysis(newAnalysisId, idToken);
        
        // Step 4: the status poll takes it from here
        setCurrentStep(4);
      }

    } catch (err) {
      setError(`Error analyzing: ${err.message}`);
      console.error(err);
      setIsAnalyzing(false);
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  };

  const analyzeTranscriptDirect = async (idToken) => {
    try {
      setCurrentStep(3);

      // Send transcript to analysis endpoint (queued for async processing)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/AnalyzeTranscript`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'AccessToken': idToken,
          },
          body: JSON.stringify({
            transcript: transcript.trim(),
            llm_provider: 'anthropic',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to analyze transcript: ${response.statusText}`);
      }

      const results = await response.json();

      if (results.status === 'error' || results.error) {
        throw new Error(results.message || 'Analysis failed');
      }

      const newAnalysisId = results.Entities?.[0] || results.analysis_id || results.id;
      if (!newAnalysisId) {
        throw new Error('Analysis queued but no analysis ID was returned');
      }

      setAnalysisId(newAnalysisId);
    } catch (err) {
      throw new Error(`Transcript analysis failed: ${err.message}`);
    }
  };

  const finalizeAnalysis = async (newAnalysisId, idToken) => {
    const { status, myJson } = await FinalizeAnalysis(idToken, newAnalysisId);

    if (status === 402) {
      const detail = myJson?.detail || {};
      throw new Error(detail.message || 'Not enough funds to run this analysis.');
    }

    if (status !== 200) {
      throw new Error(myJson?.detail || `Failed to queue analysis (${status})`);
    }
  };

  const uploadFileToGCS = (uploadUrl, file, contentType = null) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setUploadProgress(100);
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream');
      xhr.send(file);
    });
  };

  const renderAnalysisResults = () => {
    if (!analysisResults) return null;

    const researchData = parseJsonFromString(
      analysisResults.analysis?.research || analysisResults.research
    );
    const hasResearch = Boolean(
      researchData && (
        (Array.isArray(researchData.factual_issues) && researchData.factual_issues.length > 0) ||
        (Array.isArray(researchData.enhancements) && researchData.enhancements.length > 0) ||
        (Array.isArray(researchData.misinterpretation_risks) && researchData.misinterpretation_risks.length > 0)
      )
    );

    return (
      <div className="results-container">
        <h2>Analysis Results</h2>

        {/* Video Metadata */}
        {analysisResults.metadata?.metadata && (
          <div className="result-section metadata-section">
            <h3>Generated Metadata</h3>
            <div className="metadata-display">
              <div className="metadata-item">
                <label>Title</label>
                <p className="metadata-title">{analysisResults.metadata.metadata.title}</p>
              </div>
              <div className="metadata-item">
                <label>Summary</label>
                <p className="metadata-summary">{analysisResults.metadata.metadata.summary_headline}</p>
              </div>
              <div className="metadata-item">
                <label>Description</label>
                <p className="metadata-description">{analysisResults.metadata.metadata.description}</p>
              </div>
              {analysisResults.metadata.metadata.tags && analysisResults.metadata.metadata.tags.length > 0 && (
                <div className="metadata-item">
                  <label>Tags</label>
                  <div className="tags-list">
                    {analysisResults.metadata.metadata.tags.map((tag, idx) => (
                      <span key={idx} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
              {analysisResults.metadata.metadata.suggested_hashtags && analysisResults.metadata.metadata.suggested_hashtags.length > 0 && (
                <div className="metadata-item">
                  <label>Suggested Hashtags</label>
                  <div className="hashtags-list">
                    {analysisResults.metadata.metadata.suggested_hashtags.map((hashtag, idx) => (
                      <span key={idx} className="hashtag">{hashtag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="result-section executive-summary">
          <h3>Executive Summary</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Quality Score</div>
              <div className="metric-value score-7">
                {(() => {
                  const clarity = parseJsonFromString(
                    analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement
                  );
                  const avgScore = averageNumbers(
                    clarity?.scores?.hook_strength,
                    clarity?.scores?.content_clarity,
                    clarity?.scores?.pacing_quality
                  );
                  const score = analysisResults.content_analysis?.overall_score ?? avgScore;
                  return score !== null && score !== undefined ? `${Number(score).toFixed(1)}/10` : 'N/A/10';
                })()}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Predicted Retention</div>
              <div className="metric-value retention">
                {(() => {
                  const clarity = parseJsonFromString(
                    analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement
                  );
                  const avgScore = averageNumbers(
                    clarity?.scores?.hook_strength,
                    clarity?.scores?.content_clarity,
                    clarity?.scores?.pacing_quality
                  );
                  const retention = analysisResults.retention_analysis?.predicted_retention_rate ??
                    (avgScore !== null ? avgScore * 10 : null);
                  return retention !== null && retention !== undefined
                    ? `${Number(retention).toFixed(0)}%`
                    : 'N/A%';
                })()}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Issues Found</div>
              <div className="metric-value issues">
                {(() => {
                  const claims = parseJsonFromString(
                    analysisResults.analysis?.claims_risk || analysisResults.claims_risk
                  );
                  return analysisResults.issue_analysis?.issues?.length ??
                    claims?.risky_claims?.length ?? 0;
                })()}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Risk Level</div>
              <div className={`metric-value risk-${(
                analysisResults.issue_analysis?.overall_risk_level ||
                parseJsonFromString(
                  analysisResults.analysis?.claims_risk || analysisResults.claims_risk
                )?.overall_risk_level ||
                'unknown'
              ).toLowerCase()}`}>
                {analysisResults.issue_analysis?.overall_risk_level ||
                  parseJsonFromString(
                    analysisResults.analysis?.claims_risk || analysisResults.claims_risk
                  )?.overall_risk_level ||
                  'Unknown'}
              </div>
            </div>
          </div>
        </div>

        {/* Content Analysis */}
        {(analysisResults.content_analysis || analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement) && (
          <div className="result-section content-analysis">
            <h3>Content Analysis</h3>
            <p className="summary">
              {(() => {
                if (analysisResults.content_analysis?.summary) {
                  return analysisResults.content_analysis.summary;
                }
                const clarity = parseJsonFromString(
                  analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement
                );
                if (!clarity?.scores) return 'Summary not available.';
                const { hook_strength, content_clarity, pacing_quality, engagement_risk } = clarity.scores;
                return `Hook strength: ${hook_strength ?? 'N/A'}/10, clarity: ${content_clarity ?? 'N/A'}/10, pacing: ${pacing_quality ?? 'N/A'}/10, engagement risk: ${engagement_risk ?? 'N/A'}/10.`;
              })()}
            </p>
            
            {(analysisResults.content_analysis?.improvements ||
              parseJsonFromString(
                analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement
              )?.improvements) && (
              <div className="improvements-list">
                <h4>Suggested Improvements</h4>
                <ul>
                  {(analysisResults.content_analysis?.improvements ||
                    parseJsonFromString(
                      analysisResults.analysis?.clarity_engagement || analysisResults.clarity_engagement
                    )?.improvements ||
                    []).slice(0, 5).map((improvement, idx) => (
                      <li key={idx} className={`priority-${(improvement.priority || improvement.impact || '').toString().toLowerCase()}`}>
                        <span className="category">[{improvement.category || improvement.area || 'General'}]</span>
                        <span className="suggestion">{improvement.suggestion || improvement.issue}</span>
                        <span className="priority-badge">{improvement.priority || improvement.impact || 'medium'}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Issues Detection */}
        {analysisResults.issue_analysis && (
          <div className="result-section issue-detection">
            <h3>Issues Detected</h3>
            <p className="summary">{analysisResults.issue_analysis.summary}</p>
            
            {analysisResults.issue_analysis.issues && analysisResults.issue_analysis.issues.length > 0 && (
              <div className="issues-list">
                {analysisResults.issue_analysis.issues.map((issue, idx) => (
                  <div key={idx} className={`issue-card severity-${issue.severity?.toLowerCase()}`}>
                    <div className="issue-header">
                      <span className="issue-type">{issue.issue_type}</span>
                      <span className={`severity-badge severity-${issue.severity?.toLowerCase()}`}>
                        {issue.severity}
                      </span>
                    </div>
                    <p className="issue-description">{issue.description}</p>
                    <p className="issue-action">
                      <strong>Fix:</strong> {issue.recommended_action}
                    </p>
                    <p className="issue-impact">
                      <strong>Retention Impact:</strong> {issue.impact_on_retention}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Retention Analysis */}
        {analysisResults.retention_analysis && (
          <div className="result-section retention-analysis">
            <h3>Retention Analysis</h3>
            
            <div className="retention-metrics">
              <div className="metric">
                <span className="label">Predicted Retention Rate:</span>
                <span className="value">
                  {analysisResults.retention_analysis.predicted_retention_rate?.toFixed(1) || 'N/A'}%
                </span>
              </div>
            </div>

            {analysisResults.retention_analysis.drop_off_points && (
              <div className="dropoff-analysis">
                <h4>Drop-off Points</h4>
                <ul>
                  {analysisResults.retention_analysis.drop_off_points.map((point, idx) => (
                    <li key={idx}>
                      At <strong>{point}s</strong> - Users tend to leave at this point
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysisResults.retention_analysis.boring_segments && (
              <div className="segments-analysis">
                <h4>Boring Segments</h4>
                <p>{analysisResults.retention_analysis.boring_segments}</p>
              </div>
            )}

            {analysisResults.retention_analysis.engaging_segments && (
              <div className="segments-analysis engaging">
                <h4>Engaging Segments</h4>
                <p>{analysisResults.retention_analysis.engaging_segments}</p>
              </div>
            )}
          </div>
        )}

        {/* Research Findings */}
        {hasResearch && (
          <div className="result-section issue-detection">
            <h3>Research Findings</h3>

            {researchData.factual_issues && researchData.factual_issues.length > 0 && (
              <div className="issues-list">
                <h4>Factual Issues</h4>
                {researchData.factual_issues.map((issue, idx) => (
                  <div key={idx} className="issue-card severity-medium">
                    <p><strong>Claim:</strong> {issue.claim}</p>
                    <p><strong>Issue:</strong> {issue.issue}</p>
                    <p><strong>Source:</strong> {issue.source}</p>
                  </div>
                ))}
              </div>
            )}

            {researchData.enhancements && researchData.enhancements.length > 0 && (
              <div className="issues-list">
                <h4>Enhancement Suggestions</h4>
                {researchData.enhancements.map((enhancement, idx) => (
                  <div key={idx} className="issue-card severity-low">
                    <p><strong>Area:</strong> {enhancement.area}</p>
                    <p><strong>Suggestion:</strong> {enhancement.suggestion}</p>
                  </div>
                ))}
              </div>
            )}

            {researchData.misinterpretation_risks && researchData.misinterpretation_risks.length > 0 && (
              <div className="issues-list">
                <h4>Misinterpretation Risks</h4>
                {researchData.misinterpretation_risks.map((risk, idx) => (
                  <div key={idx} className="issue-card severity-high">
                    <p><strong>Phrase:</strong> "{risk.phrase}"</p>
                    <p><strong>Risk:</strong> {risk.risk}</p>
                    <p><strong>Safer Alternative:</strong> "{risk.safer_alternative}"</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Captions */}
        {analysisResults.captions && analysisResults.captions.captions && (
          <div className="result-section captions">
            <h3>Extracted Captions</h3>
            <div className="captions-list">
              {analysisResults.captions.captions.slice(0, 10).map((caption, idx) => (
                <div key={idx} className="caption-item">
                  <span className="time">{caption.start_time.toFixed(1)}s</span>
                  <span className="text">{caption.text}</span>
                  <span className="confidence">{(caption.confidence * 100)?.toFixed(0)}%</span>
                </div>
              ))}
              {analysisResults.captions.captions.length > 10 && (
                <p className="more-captions">
                  ... and {analysisResults.captions.captions.length - 10} more captions
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="video-analysis-container">
      <div className="header">
        <h1>Video Analysis</h1>
        <p>Analyze your videos using AI-powered multi-agent system</p>
      </div>

      <div className="upload-section">
        {/* Mode Selection */}
        <div className="mode-selector">
          <label>Choose Analysis Method:</label>
          <div className="mode-buttons">
            <button 
              className={`mode-btn ${analysisMode === 'video' ? 'active' : ''}`}
              onClick={() => {
                setAnalysisMode('video');
                setTranscript('');
                setTranscriptFile(null);
                setUploadProgress(0);
                setError(null);
              }}
            >
              Upload Video
            </button>
            <button 
              className={`mode-btn ${analysisMode === 'transcript' ? 'active' : ''}`}
              onClick={() => {
                setAnalysisMode('transcript');
                setVideoFile(null);
                setVideoPreview(null);
                setUploadProgress(0);
                setError(null);
              }}
            >
              Transcript
            </button>
          </div>
        </div>

        {analysisMode === 'video' ? (
          /* Video Upload Mode */
          <div className="upload-area">
            {videoPreview ? (
              <div className="video-preview">
                <video src={videoPreview} controls width="300" />
                <p className="file-name">{videoFile?.name}</p>
                <button 
                  onClick={() => fileInputRef.current?.click()} 
                  className="change-btn"
                >
                  Change Video
                </button>
              </div>
            ) : (
              <div 
                className="upload-placeholder"
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer' }}
              >
                <div className="upload-icon"><Video size={32} aria-hidden="true" /></div>
                <p>Click to upload video</p>
                <span className="file-type">MP4, WebM, or other video format</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          /* Transcript Input Mode */
          <div className="transcript-input-area">
            <div className="transcript-upload">
              <label>Upload transcript file (recommended for large text):</label>
              {transcriptFile ? (
                <div className="transcript-file-card">
                  <div className="file-name">{transcriptFile.name}</div>
                  <button
                    onClick={() => transcriptFileInputRef.current?.click()}
                    className="change-btn"
                    type="button"
                  >
                    Change File
                  </button>
                </div>
              ) : (
                <div
                  className="upload-placeholder transcript-upload-placeholder"
                  onClick={() => transcriptFileInputRef.current?.click()}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="upload-icon"><FileText size={32} aria-hidden="true" /></div>
                  <p>Click to upload transcript</p>
                  <span className="file-type">TXT, SRT, VTT, or Markdown</span>
                </div>
              )}
              <input
                ref={transcriptFileInputRef}
                type="file"
                accept=".txt,.md,.srt,.vtt,text/plain,text/vtt"
                onChange={handleTranscriptFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="transcript-divider">or</div>

            <label htmlFor="transcript-input">Paste or type your video transcript:</label>
            <textarea
              id="transcript-input"
              className="transcript-textarea"
              value={transcript}
              onChange={(e) => {
                setTranscript(e.target.value);
                if (transcriptFile) {
                  setTranscriptFile(null);
                }
                setError(null);
              }}
              placeholder="Enter the full transcript of your video here. Include speaker names and timing information if available (e.g., '[00:00] Speaker Name: Hello everyone...')."
              rows="10"
            />
            <div className="transcript-helper">
              <p>Tip: Uploading a file is faster for large transcripts.</p>
              <p>Include natural pauses, speaker names, and any non-verbal cues if relevant.</p>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="analysis-controls">
          <button 
            onClick={handleAnalyze}
            disabled={
              (analysisMode === 'video' && !videoFile) ||
              (analysisMode === 'transcript' && !transcriptFile && !transcript.trim()) ||
              isAnalyzing
            }
            className="analyze-btn"
          >
            {isAnalyzing ? (
              <>
                <span className="spinner"></span>
                Analyzing...
              </>
            ) : analysisMode === 'video' ? (
              'Analyze Video'
            ) : transcriptFile ? (
              'Analyze Transcript File'
            ) : (
              'Analyze Transcript'
            )}
          </button>
        </div>
      </div>

      {isAnalyzing && (
        <div className="analyzing-container">
          <div className="analyzing-content">
            <div className="spinner-large"></div>
            <h3>
              Analyzing {analysisMode === 'video' ? 'Video' : (isTranscriptUpload ? 'Transcript File' : 'Transcript')}...
            </h3>
            <p>{analysisMode === 'video' 
              ? 'This may take 30 seconds to a few minutes depending on video length'
              : (isTranscriptUpload
                ? 'Uploading transcript and running AI agents...'
                : 'Analyzing transcript using AI agents...')}
            </p>
            <div className="progress-steps">
              {analysisMode === 'video' || isTranscriptUpload ? (
                <>
                  <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-text">Creating Analysis</span>
                  </div>
                  <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-text">
                      Uploading {analysisMode === 'video' ? 'File' : 'Transcript'} {uploadProgress > 0 && `(${uploadProgress.toFixed(0)}%)`}
                    </span>
                  </div>
                  <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-text">Starting Analysis</span>
                  </div>
                  <div className={`step ${currentStep >= 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
                    <span className="step-number">4</span>
                    <span className="step-text">Processing</span>
                  </div>
                  <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
                    <span className="step-number">5</span>
                    <span className="step-text">Complete</span>
                  </div>
                </>
              ) : (
                <>
                  <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-text">Processing Transcript</span>
                  </div>
                  <div className={`step ${currentStep >= 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-text">Running Analysis Agents</span>
                  </div>
                  <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-text">Complete</span>
                  </div>
                </>
              )}
            </div>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
          </div>
        </div>
      )}

      {analysisResults && !isAnalyzing && renderAnalysisResults()}
    </div>
  );
};

export default VideoAnalysis;
